import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Navigation from "./components/Navigation";
import DashboardPage from "./pages/DashboardPage";
import JourneyMapPage from "./pages/JourneyMapPage";
import AgendaPage from "./pages/AgendaPage";
import OperationPage from "./pages/OperationPage";
import CreativesPage from "./pages/CreativesPage";
import LibraryPage from "./pages/LibraryPage";
import ProfilePage from "./pages/ProfilePage";
import AdminPage from "./pages/AdminPage";
import AuthPage from "./pages/AuthPage";
import AccessBlockedPage from "./pages/AccessBlockedPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SubscriptionGatePage from "./pages/SubscriptionGatePage";
import InvitePage from "./pages/InvitePage";
import MentorSignupPage from "./pages/MentorSignupPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import AnnouncementPopup from "./components/AnnouncementPopup";
import { Toaster } from "./components/ui/sonner";
import "./App.css";

function ProtectedApp() {
  const { user, loading, bootError, retryBootstrap, logout } = useAuth();
  const location = useLocation();

  // Emergent Google OAuth callback. Must run BEFORE any session check
  // because the AuthContext bootstrap would otherwise fail (there is no
  // Supabase session yet) and show the loading or login screen.
  if (
    location.pathname === "/auth/callback" ||
    (location.hash && location.hash.includes("session_id="))
  ) {
    return <AuthCallbackPage />;
  }

  // Reset-password link works even without a full app session — it uses the
  // recovery session Supabase creates from the URL fragment.
  if (location.pathname === "/reset-password") {
    return <ResetPasswordPage />;
  }

  // Public onboarding routes — must work regardless of session state so
  // brand new visitors can sign up as mentor or via a student invite link.
  if (location.pathname.startsWith("/convite/")) {
    return (
      <Routes>
        <Route path="/convite/:token" element={<InvitePage />} />
      </Routes>
    );
  }
  if (location.pathname === "/mentor-signup") {
    return <MentorSignupPage />;
  }

  if (bootError) {
    return (
      <div
        data-testid="boot-error-screen"
        className="flex items-center justify-center min-h-screen bg-[#0B0C10] p-6"
      >
        <div className="max-w-md text-center space-y-4 bg-[#14161C] border border-[#282D3D] rounded-2xl p-6">
          <h1 className="text-lg sm:text-xl font-extrabold text-white">
            Não conseguimos carregar o Modo Caverna.
          </h1>
          <p className="text-xs text-gray-400">
            {bootError.message} Tente novamente ou entre novamente na sua conta.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              data-testid="boot-retry-btn"
              onClick={retryBootstrap}
              className="px-4 py-2 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase tracking-wider"
            >
              Tentar novamente
            </button>
            <button
              data-testid="boot-signout-btn"
              onClick={async () => {
                await logout();
                retryBootstrap();
              }}
              className="px-4 py-2 rounded-lg bg-[#1F2330] hover:bg-[#282F42] border border-[#3E4760] text-white font-bold text-xs uppercase tracking-wider"
            >
              Sair e entrar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        data-testid="boot-loading-screen"
        className="flex items-center justify-center min-h-screen bg-[#0B0C10]"
      >
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-3 border-[#FF6B00] border-t-transparent rounded-full animate-spin" />
          <span className="text-xs font-semibold text-gray-400">Modo Caverna...</span>
        </div>
      </div>
    );
  }

  // `user` alone is enough: Google sign-in uses a cookie session (no Supabase session).
  if (!user) {
    return <AuthPage />;
  }

  // Access gate: a student whose mentorship was never approved, has
  // expired, or was removed cannot access ANY authenticated area. Staff
  // (admin / platform_owner) is always allowed through so they can
  // administer the system. We also exempt /perfil so the blocked user
  // can review their profile / contact mentor.
  const isStaff = user.role === "admin" || user.role === "platform_owner";
  const accessAllowed =
    user.has_access !== false || isStaff || location.pathname === "/perfil";
  if (!accessAllowed) {
    return <AccessBlockedPage message={user.access_message} user={user} onLogout={logout} />;
  }

  // Mentor keeps "/" → "/admin". Owner lands on "/" (Minha Operação / Journey)
  // so he can play Modo Caverna without losing Owner permissions. Owner still
  // reaches /admin via the "Dono" nav button.
  const homeRedirect = user.role === "admin" ? "/admin" : null;

  return (
    <div className="min-h-screen bg-[#0B0C10] text-[#F3F4F6] flex flex-col font-sans selection:bg-[#FF6B00] selection:text-black">
      {/* Solid strip behind the iPhone status bar so content never scrolls under it. */}
      <div aria-hidden className="md:hidden fixed top-0 inset-x-0 z-[55] bg-[#0B0C10]" style={{ height: "env(safe-area-inset-top)" }} />
      <Navigation />
      <AnnouncementPopup />
      <main className="flex-1 safe-main">
        <Routes>
          <Route path="/" element={homeRedirect ? <Navigate to={homeRedirect} replace /> : <JourneyMapPage />} />
          <Route path="/jornada" element={<JourneyMapPage />} />
          <Route path="/painel" element={<DashboardPage />} />
          <Route path="/agenda" element={<AgendaPage />} />
          <Route path="/missoes" element={<Navigate to="/agenda" replace />} />
          <Route path="/operacao" element={<OperationPage />} />
          <Route path="/biblioteca" element={<LibraryPage />} />
          <Route path="/criativos" element={<Navigate to="/operacao?tab=criativos" replace />} />
          <Route path="/perfil" element={<ProfilePage />} />
          <Route path="/assinatura" element={<ProfilePage />} />
          <Route
            path="/admin"
            element={isStaff ? <AdminPage /> : <Navigate to="/" replace />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ProtectedApp />
        <Toaster theme="dark" position="top-center" offset="calc(env(safe-area-inset-top) + 12px)" />
      </BrowserRouter>
    </AuthProvider>
  );
}
